import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Filter } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchCiterneEcart } from '@/store/thunks/chartThunks';
import type { CiterneEcartGroup, CiterneEcartRow } from '@/types';
import { Button, Card, DataTable, Input } from '@/components/ui';

function formatNum(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) : '0';
}

const detailColumns = [
  { key: 'date', label: 'Date jauge', render: (v: unknown) => (v ? new Date(v as string).toLocaleDateString('fr-FR') : '—') },
  { key: 'sortie', label: 'Total sortie', render: (v: unknown) => formatNum(Number(v)) },
  { key: 'stock_calcule', label: 'Stock calculé', render: (v: unknown) => formatNum(Number(v)) },
  { key: 'stock_reel', label: 'Stock réel', render: (v: unknown) => formatNum(Number(v)) },
  { key: 'stock_theorique', label: 'Stock théorique', render: (v: unknown) => formatNum(Number(v)) },
  { key: 'ecart', label: 'Écart', render: (v: unknown) => formatNum(Number(v)) },
];

export default function CiterneEcartSection() {
  const dispatch = useAppDispatch();
  const groups = useAppSelector((s: RootState) => s.chart.citernesEcart);
  const [dateStart, setDateStart] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [dateEnd, setDateEnd] = useState(() => {
    const d = new Date();
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return last.toISOString().slice(0, 10);
  });

  useEffect(() => {
    dispatch(fetchCiterneEcart({ date_b: dateStart, date_f: dateEnd }));
  }, [dispatch]);

  const handleFilter = () => {
    dispatch(fetchCiterneEcart({ date_b: dateStart, date_f: dateEnd }));
  };

  const totalEcart = useMemo(
    () => groups.reduce((sum, g) => sum + (g.totalEcart ?? 0), 0),
    [groups]
  );
  const nbReleves = useMemo(
    () => groups.reduce((sum, g) => sum + (g.nbReleves ?? 0), 0),
    [groups]
  );
  const citernesConcernees = groups.length;
  const ecartMoyen = nbReleves > 0 ? totalEcart / nbReleves : 0;
  const ecartMax = groups.length > 0 ? Math.max(...groups.map((g) => g.ecartMax ?? 0)) : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="grid w-full max-w-xl grid-cols-2 gap-4">
          <Input
            label="Date début"
            type="date"
            id="ecart-date-start"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
          />
          <Input
            label="Date fin"
            type="date"
            id="ecart-date-end"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
          />
        </div>
        <Button onClick={handleFilter}>
          <Filter className="mr-2 h-4 w-4" />
          Filtrer
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card className="border-teal-200 bg-teal-50/50 p-4">
          <p className="text-sm font-medium text-slate-600">Écart total</p>
          <p className="text-xl font-semibold text-teal-800">{formatNum(totalEcart)} L</p>
        </Card>
        <Card className="border-slate-200 bg-slate-50/50 p-4">
          <p className="text-sm font-medium text-slate-600">Relevés</p>
          <p className="text-xl font-semibold text-slate-800">{nbReleves}</p>
        </Card>
        <Card className="border-slate-200 bg-slate-50/50 p-4">
          <p className="text-sm font-medium text-slate-600">Citernes concernées</p>
          <p className="text-xl font-semibold text-slate-800">{citernesConcernees}</p>
        </Card>
        <Card className="border-amber-200 bg-amber-50/50 p-4">
          <p className="text-sm font-medium text-slate-600">Écart moyen / relevé</p>
          <p className="text-xl font-semibold text-amber-800">{formatNum(ecartMoyen)} L</p>
        </Card>
        <Card className="col-span-2 border-red-100 bg-red-50/50 p-4 sm:col-span-4">
          <p className="text-sm font-medium text-slate-600">Écart max (anomalie)</p>
          <p className="text-xl font-semibold text-red-700">{formatNum(ecartMax)} L</p>
        </Card>
      </div>

      <div className="space-y-3">
        {groups.length === 0 ? (
          <Card>
            <p className="py-6 text-center text-sm text-slate-500">Aucun écart sur la période</p>
          </Card>
        ) : (
          groups.map((g: CiterneEcartGroup) => {
            const code = g.citerne?.code ?? `#${g.citerne?.id ?? '—'}`;
            const tableData = (g.releves ?? []).map((row: CiterneEcartRow, i: number) => ({
              ...row,
              _key: `${g.citerne?.id ?? 0}-${String(row.date)}-${i}`,
            })) as (CiterneEcartRow & { _key: string } & Record<string, unknown>)[];

            return (
              <details
                key={String(g.citerne?.id ?? code)}
                className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm open:shadow-md"
              >
                <summary className="flex cursor-pointer list-none flex-wrap items-stretch justify-between gap-3 border-b border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-slate-50/90 px-4 py-4 sm:items-center sm:gap-4 sm:px-5 sm:py-4">
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center self-start rounded-xl border border-slate-200/90 bg-white text-slate-500 shadow-sm transition-colors group-open:border-teal-300/70 group-open:bg-teal-50/80 group-open:text-teal-800 sm:self-center"
                    aria-hidden
                  >
                    <ChevronDown className="h-5 w-5 shrink-0 transition-transform duration-200 ease-out group-open:rotate-180" strokeWidth={2.25} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-semibold tracking-tight text-slate-900 sm:text-xl">{code}</h3>
                    <p className="mt-0.5 text-sm text-slate-500">
                      <span className="font-semibold tabular-nums text-slate-700">{g.nbReleves ?? 0}</span>{' '}
                      relevé{(g.nbReleves ?? 0) !== 1 ? 's' : ''} sur la période
                    </p>
                  </div>
                  <div className="grid w-full grid-cols-1 gap-2 sm:w-auto sm:min-w-[28rem] sm:grid-cols-3 sm:gap-3">
                    <div className="flex min-w-0 flex-col rounded-xl border border-teal-200/90 bg-teal-50/40 px-3 py-2.5 shadow-sm ring-1 ring-teal-900/5 sm:px-4 sm:py-3">
                      <span className="text-[0.65rem] font-bold uppercase tracking-[0.12em] text-teal-800/90">
                        Σ écart
                      </span>
                      <span className="mt-1 flex items-baseline gap-1.5">
                        <span className="text-xl font-bold tabular-nums tracking-tight text-teal-950 sm:text-2xl">
                          {formatNum(g.totalEcart ?? 0)}
                        </span>
                        <span className="text-sm font-semibold text-teal-800">L</span>
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-col rounded-xl border border-slate-200/90 bg-white px-3 py-2.5 shadow-sm ring-1 ring-slate-900/5 sm:px-4 sm:py-3">
                      <span className="text-[0.65rem] font-bold uppercase tracking-[0.12em] text-slate-500">
                        Moyenne
                      </span>
                      <span className="mt-1 flex items-baseline gap-1.5">
                        <span className="text-xl font-bold tabular-nums tracking-tight text-slate-900 sm:text-2xl">
                          {formatNum(g.ecartMoyen ?? 0)}
                        </span>
                        <span className="text-sm font-semibold text-slate-600">L</span>
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-col rounded-xl border border-amber-200/90 bg-amber-50/50 px-3 py-2.5 shadow-sm ring-1 ring-amber-900/5 sm:px-4 sm:py-3">
                      <span className="text-[0.65rem] font-bold uppercase tracking-[0.12em] text-amber-800/90">
                        Pic max
                      </span>
                      <span className="mt-1 flex items-baseline gap-1.5">
                        <span className="text-xl font-bold tabular-nums tracking-tight text-amber-950 sm:text-2xl">
                          {formatNum(g.ecartMax ?? 0)}
                        </span>
                        <span className="text-sm font-semibold text-amber-900">L</span>
                      </span>
                    </div>
                  </div>
                </summary>
                <div className="p-3 sm:p-4">
                  <DataTable<CiterneEcartRow & { _key: string } & Record<string, unknown>>
                    columns={detailColumns as never}
                    data={tableData}
                    keyField="_key"
                    emptyMessage="Aucun relevé"
                  />
                </div>
              </details>
            );
          })
        )}
      </div>
    </div>
  );
}
